import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    Switch,
    Chip,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Grid,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import locationService from '../../services/locationService';

function StateManagement({ onDataChange }) {
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [countryFilter, setCountryFilter] = useState('all');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingState, setEditingState] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [formData, setFormData] = useState({
        country_id: '',
        name: '',
        code: '',
        status: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const { handleApiCallWithLoading, handleApiCall, showSuccess, showError } = useApiHandler();

    useEffect(() => {
        loadStates();
        loadCountries();
    }, []);

    const loadStates = async () => {
        try {
            setLoading(true);
            // /location-management/states is paginated (10 by default) but this
            // page pages through the rows itself, so load them all (36 states).
            const params = { per_page: 100 };
            if (searchTerm) params.search = searchTerm;
            if (statusFilter !== 'all') params.status = statusFilter === 'active';
            if (countryFilter !== 'all') params.country_id = countryFilter;

            const response = await locationService.getStates(params);
            if (response.success) {
                setStates(response.data);
            }
        } catch (error) {
            console.error('Error loading states:', error);
            showError('Error loading states');
        } finally {
            setLoading(false);
        }
    };

    const loadCountries = async () => {
        try {
            const response = await locationService.getCountriesDropdown();
            if (response.success) {
                setCountries(response.data);
            }
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    };

    const handleSearch = () => {
        loadStates();
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setCountryFilter('all');
        loadStates();
    };

    const handleOpenDialog = (state = null) => {
        if (state) {
            setEditingState(state);
            setFormData({
                country_id: state.country_id,
                name: state.name,
                code: state.code || '',
                status: state.status
            });
        } else {
            setEditingState(null);
            setFormData({
                country_id: '',
                name: '',
                code: '',
                status: true
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingState(null);
        setFormData({
            country_id: '',
            name: '',
            code: '',
            status: true
        });
        setFormErrors({});
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.country_id) {
            errors.country_id = 'Country is required';
        }
        
        if (!formData.name.trim()) {
            errors.name = 'State name is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const stateData = {
                ...formData,
                country_id: parseInt(formData.country_id)
            };

            let response;
            if (editingState) {
                response = await locationService.updateState(editingState.state_id, stateData);
            } else {
                response = await locationService.createState(stateData);
            }

            if (response.success) {
                const action = editingState ? 'updated' : 'created';
                showSuccess(
                    `State ${action} successfully`
                );
                handleCloseDialog();
                loadStates();
                if (onDataChange) onDataChange();
            }
        } catch (error) {
            console.error('Error saving state:', error);
            const message = error.response?.data?.message || 'Failed to save state. Please try again.';
            showError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (state) => {
        if (!window.confirm(`Are you sure you want to delete "${state.name}"?`)) {
            return;
        }

        try {
            await locationService.deleteState(state.state_id);
            showSuccess('State deleted successfully');
            loadStates();
            if (onDataChange) onDataChange();
        } catch (error) {
            console.error('Error deleting state:', error);
            const message = error.response?.data?.message || 'Failed to delete state. Please try again.';
            showError(message);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredStates = states.filter(state => {
        const matchesSearch = !searchTerm || 
            state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (state.code && state.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (state.country_name && state.country_name.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesStatus = statusFilter === 'all' || 
            (statusFilter === 'active' && state.status) ||
            (statusFilter === 'inactive' && !state.status);
        
        const matchesCountry = countryFilter === 'all' || 
            state.country_id.toString() === countryFilter;
        
        return matchesSearch && matchesStatus && matchesCountry;
    });

    return (
        <Box>
            {/* Header Actions */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search states..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 250 }}
                />
                
                <TextField
                    select
                    size="small"
                    label="Country"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="all">All Countries</MenuItem>
                    {countries.map((country) => (
                        <MenuItem key={country.country_id} value={country.country_id.toString()}>
                            {country.name}
                        </MenuItem>
                    ))}
                </TextField>
                
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 120 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>

                <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                >
                    Search
                </Button>

                <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add State
                </Button>
            </Box>

            {/* States Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Cities</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : filteredStates.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No states found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredStates.map((state) => (
                                <TableRow key={state.state_id}>
                                    <TableCell>{state.name}</TableCell>
                                    <TableCell>{state.code || '-'}</TableCell>
                                    <TableCell>{state.country_name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={state.status ? 'Active' : 'Inactive'}
                                            color={state.status ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{state.cities_count || 0}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(state)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={state.cities_count > 0 ? "Cannot delete state with existing cities" : "Delete"}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(state)}
                                                    disabled={state.cities_count > 0}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredStates.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingState ? 'Edit State' : 'Add New State'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Country"
                                value={formData.country_id}
                                onChange={(e) => handleInputChange('country_id', e.target.value)}
                                error={!!formErrors.country_id}
                                helperText={formErrors.country_id}
                                required
                            >
                                <MenuItem value="">Select Country</MenuItem>
                                {countries.map((country) => (
                                    <MenuItem key={country.country_id} value={country.country_id}>
                                        {country.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="State Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="State Code"
                                value={formData.code}
                                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                                helperText="Optional state code (e.g., CA, NY, MH)"
                                inputProps={{ maxLength: 10 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.status}
                                        onChange={(e) => handleInputChange('status', e.target.checked)}
                                    />
                                }
                                label="Active Status"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={20} /> : (editingState ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default StateManagement; 